import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, Contact, CornerDownLeft, Globe2, Megaphone, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { globalCrmSearch } from '@/features/vendorCrm/api';
import type { GlobalCrmSearchResult } from '@/features/vendorCrm/types';
import { useAppView } from '@/contexts/AppViewContext';
import { canAccessAppPath } from '@/lib/appAccess';
import { NAV_PAGES, searchNavPages } from '@/lib/navCatalog';

const resultIcons: Record<GlobalCrmSearchResult['entityType'], typeof Building2> = {
  vendor: Building2,
  campaign: Megaphone,
  contact: Contact,
  domain: Globe2,
};

const resultLabels: Record<GlobalCrmSearchResult['entityType'], string> = {
  vendor: 'Vendors',
  campaign: 'Campaigns',
  contact: 'Contacts',
  domain: 'Domains',
};

export function GlobalQuickSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { viewMode } = useAppView();
  const trimmedQuery = query.trim();

  // Pages match locally, so jumping is instant and needs no network. Filtering
  // by view mode keeps restricted routes out of a standard user's results the
  // same way the sidebar and the route guard do.
  const pageMatches = useMemo(
    () => searchNavPages(trimmedQuery, NAV_PAGES.filter((page) => canAccessAppPath(viewMode, page.path))).slice(0, 6),
    [trimmedQuery, viewMode],
  );

  const search = useQuery({
    queryKey: ['crm', 'global-search', trimmedQuery],
    queryFn: () => globalCrmSearch(trimmedQuery),
    enabled: open && trimmedQuery.length >= 2,
    staleTime: 30_000,
  });

  const grouped = useMemo(() => {
    const groups = new Map<GlobalCrmSearchResult['entityType'], GlobalCrmSearchResult[]>();
    for (const result of search.data ?? []) {
      const values = groups.get(result.entityType) ?? [];
      if (values.length < 5) values.push(result);
      groups.set(result.entityType, values);
    }
    return groups;
  }, [search.data]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const openResult = (result: GlobalCrmSearchResult) => {
    navigate(result.path);
    close();
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex min-h-11 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        aria-label="Search pages, vendors, campaigns, contacts, and domains"
        title="Search ComplianceMagic (Ctrl+K)"
      >
        <Search className="h-4 w-4" aria-hidden="true" />
        <span className="hidden lg:inline">Search</span>
        <kbd className="hidden rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-sans text-[11px] font-normal text-gray-500 xl:inline">Ctrl K</kbd>
      </button>

      {open && (
        <div className="fixed inset-x-3 top-16 z-50 mx-auto w-auto max-w-xl rounded-xl border border-gray-300 bg-white shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[32rem]" role="dialog" aria-label="Search ComplianceMagic">
          <div className="flex items-center gap-2 border-b border-gray-200 px-3">
            <Search className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Jump to a page, or find a vendor or campaign"
              className="h-12 min-w-0 flex-1 border-0 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-500"
              role="combobox"
              aria-expanded="true"
              aria-controls="global-search-results"
              aria-autocomplete="list"
            />
            <button type="button" onClick={close} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label="Close search">
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div id="global-search-results" role="listbox" className="max-h-[65vh] overflow-y-auto p-2">
            {pageMatches.length > 0 && (
              <section aria-labelledby="search-pages" className="mb-2">
                <h2 id="search-pages" className="px-3 pb-1 pt-2 text-xs font-semibold text-gray-500">Pages</h2>
                {pageMatches.map((page) => (
                  <button
                    key={page.path}
                    type="button"
                    role="option"
                    aria-selected="false"
                    onClick={() => { navigate(page.path); close(); }}
                    className="group flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                  >
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-600">
                      <CornerDownLeft className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">{page.label}</span>
                      <span className="block truncate text-xs text-gray-600">{page.group}</span>
                    </span>
                    <span className="hidden shrink-0 font-mono text-xs text-gray-500 group-hover:inline">{page.path}</span>
                  </button>
                ))}
              </section>
            )}
            {trimmedQuery.length < 2 ? (
              pageMatches.length > 0 ? null : (
              <p className="px-3 py-8 text-center text-sm text-gray-600">Type to jump to a page, or two characters to search vendors and campaigns.</p>
              )
            ) : search.isLoading ? (
              <div className="space-y-2 p-2" aria-label="Searching">
                {[0, 1, 2].map((item) => <div key={item} className="h-12 animate-pulse rounded-md bg-gray-100 motion-reduce:animate-none" />)}
              </div>
            ) : search.isError ? (
              <div className="px-3 py-8 text-center">
                <p className="text-sm font-medium text-red-800">Search is temporarily unavailable</p>
                <button type="button" onClick={() => void search.refetch()} className="mt-2 text-sm font-semibold text-blue-700 hover:text-blue-800">Try again</button>
              </div>
            ) : grouped.size === 0 ? (
              pageMatches.length > 0 ? null : (
                <p className="px-3 py-8 text-center text-sm text-gray-600">Nothing matches “{trimmedQuery}”.</p>
              )
            ) : (
              Array.from(grouped.entries()).map(([type, results]) => (
                <section key={type} aria-labelledby={`search-${type}`} className="mb-2 last:mb-0">
                  <h2 id={`search-${type}`} className="px-3 pb-1 pt-2 text-xs font-semibold text-gray-500">{resultLabels[type]}</h2>
                  {results.map((result) => {
                    const Icon = resultIcons[result.entityType];
                    return (
                      <button
                        key={`${result.entityType}-${result.entityId}`}
                        type="button"
                        role="option"
                        aria-selected="false"
                        onClick={() => openResult(result)}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                      >
                        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-600"><Icon className="h-4 w-4" aria-hidden="true" /></span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-gray-900">{result.label}</span>
                          <span className="block truncate text-xs text-gray-600">{result.context}</span>
                        </span>
                      </button>
                    );
                  })}
                </section>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
